import axiosInstance from "..";

/**
 * Patient feedback & complaints (NABH AAC.6.a) — see root PATIENT_FEEDBACK.md
 * §4 "Patient portal". Every route is on the main instance (patient JWT).
 *
 * Unlike the older api modules these functions do NOT swallow failures: they
 * throw an Error whose `message` is the backend's `{ success:false, message }`
 * (e.g. "You have already rated this visit" on 409, "Please rate: …" on 400)
 * so the screen can show it inline. `error.status` carries the HTTP status.
 */
function toApiError(err, fallback) {
  const error = new Error(
    err?.response?.data?.message || err?.message || fallback
  );
  error.status = err?.response?.status;
  error.data = err?.response?.data;
  return error;
}

/** GET /patient-feedback/me → { pending, visits, surveys, responses } */
export async function getFeedbackOverview() {
  try {
    const response = await axiosInstance.get("/patient-feedback/me");
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not load your feedback");
  }
}

/**
 * POST /patient-feedback/me/responses
 * payload: { requestId? | appointmentId? | clinicId?, answers:[{key,rating|answer}], comment? }
 * → { message, responseId, analysis:{ overall, ratings:[{key,text,rating}] } }
 */
export async function submitFeedback(payload) {
  try {
    const response = await axiosInstance.post(
      "/patient-feedback/me/responses",
      payload
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not submit your feedback");
  }
}

/** GET /patient-feedback/me/responses → { responses } */
export async function getMyFeedback() {
  try {
    const response = await axiosInstance.get("/patient-feedback/me/responses");
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not load your feedback");
  }
}

/** GET /patient-feedback/me/clinics → { clinics:[{_id,name,city}] } */
export async function getMyClinics() {
  try {
    const response = await axiosInstance.get("/patient-feedback/me/clinics");
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not load your clinics");
  }
}

/**
 * POST /patient-feedback/me/complaints
 * payload: { clinicId? | appointmentId?, category, subject, description, relation? }
 * → { message, referenceId, complaint }
 */
export async function createComplaint(payload) {
  try {
    const response = await axiosInstance.post(
      "/patient-feedback/me/complaints",
      payload
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not register your complaint");
  }
}

/** GET /patient-feedback/me/complaints → { complaints } */
export async function getMyComplaints() {
  try {
    const response = await axiosInstance.get("/patient-feedback/me/complaints");
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not load your complaints");
  }
}

/* ---------------------------------------------------------------------------
 * Public (logged-out) survey — the link in the SMS / email / QR. Same axios
 * instance (a stale Bearer header is ignored by the /p routes). Contract:
 * PATIENT_FEEDBACK.md §4 "Public".
 * -------------------------------------------------------------------------*/

/** GET /p/feedback/:token → { clinicName, doctorName, patientFirstName, visitDate, surveyName, questions, alreadySubmitted, expiresAt } */
export async function getPublicSurvey(token) {
  try {
    const response = await axiosInstance.get(
      `/p/feedback/${encodeURIComponent(token)}`
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "This link is invalid or has expired");
  }
}

/** POST /p/feedback/:token { answers, comment? } → { message, responseId, analysis } */
export async function submitPublicSurvey(token, payload) {
  try {
    const response = await axiosInstance.post(
      `/p/feedback/${encodeURIComponent(token)}`,
      payload
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not submit your feedback");
  }
}

/** POST /p/feedback/:token/complaint → { message, referenceId, status } */
export async function submitPublicComplaint(token, payload) {
  try {
    const response = await axiosInstance.post(
      `/p/feedback/${encodeURIComponent(token)}/complaint`,
      payload
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not register your complaint");
  }
}

/** GET /p/feedback/clinic/:clinicId → { clinicName, surveyName, questions } (standing QR) */
export async function getClinicSurvey(clinicId) {
  try {
    const response = await axiosInstance.get(
      `/p/feedback/clinic/${encodeURIComponent(clinicId)}`
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "This clinic's survey is not available");
  }
}

/** POST /p/feedback/clinic/:clinicId { answers, comment?, name?, phone? } */
export async function submitClinicSurvey(clinicId, payload) {
  try {
    const response = await axiosInstance.post(
      `/p/feedback/clinic/${encodeURIComponent(clinicId)}`,
      payload
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not submit your feedback");
  }
}

/** POST /p/feedback/clinic/:clinicId/complaint { name?, phone?, category, subject, description, relation? } */
export async function submitClinicComplaint(clinicId, payload) {
  try {
    const response = await axiosInstance.post(
      `/p/feedback/clinic/${encodeURIComponent(clinicId)}/complaint`,
      payload
    );
    return response.data;
  } catch (err) {
    throw toApiError(err, "Could not register your complaint");
  }
}
